"use client"

import * as React from "react"
import { LucideIcon } from 'lucide-react'
import {
  UICard,
  UICardContent,
  UICardFooter,
  UICardHeader,
  UICardTitle,
  UICardDescription
} from './ui-card'
import { UIButton } from '../buttons/ui-button'
import { UIBadge } from '../display/ui-badge'
import { UITypography } from '../display/ui-typography'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UICardProductProps
  extends Omit<React.ComponentProps<typeof UICard>, 'children'> {
  title: string
  description?: string
  icon?: LucideIcon
  imageUrl?: string
  imageAlt?: string
  badge?: string
  badgeVariant?: 'default' | 'accent' | 'secondary'
  actionText?: string
  onAction?: () => void
  actionVariant?: 'default' | 'accent' | 'outline'
  secondaryActionText?: string
  onSecondaryAction?: () => void
  children?: React.ReactNode
}

/**
 * Enhanced Product Card Component
 * Perfect for showcasing products, features, and services
 * Based on shadcn/ui Product Card block with Seahorse branding
 */
export const UICardProduct = React.forwardRef<HTMLDivElement, UICardProductProps>(
  ({
    title,
    description,
    icon: Icon,
    imageUrl,
    imageAlt,
    badge,
    badgeVariant = 'default',
    actionText = 'Learn More',
    onAction,
    actionVariant = 'default',
    secondaryActionText,
    onSecondaryAction,
    className,
    children,
    ...props
  }, ref) => {
    return (
      <UICard
        ref={ref}
        className={cn(
          "group relative overflow-hidden",
          "hover:shadow-xl dark:hover:shadow-primary/10",
          "transition-shadow duration-300",
          className
        )}
        {...props}
      >
        {/* Header with visual element */}
        <UICardHeader className="pb-4">
          <UIBox className="flex items-start justify-between">
            <UIBox className="flex items-center gap-3">
              {/* Icon or Image */}
              {Icon && !imageUrl && (
                <UIBox className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-accent/10 dark:bg-accent/20">
                  <Icon className="h-6 w-6 text-accent" />
                </UIBox>
              )}

              {imageUrl && (
                <UIBox className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg overflow-hidden bg-muted">
                  <img
                    src={imageUrl}
                    alt={imageAlt || title}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                </UIBox>
              )}

              <UIBox className="min-w-0 flex-1">
                <UICardTitle className="group-hover:text-accent transition-colors">
                  <UITypography variant="h3" size="lg" weight="semibold">
                    {title}
                  </UITypography>
                </UICardTitle>
              </UIBox>
            </UIBox>

            {/* Badge */}
            {badge && (
              <UIBadge variant={badgeVariant}>
                {badge}
              </UIBadge>
            )}
          </UIBox>
        </UICardHeader>

        {/* Content */}
        <UICardContent className="pb-4">
          {description && (
            <UICardDescription className="leading-relaxed">
              <UITypography variant="p" size="default" color="muted">
                {description}
              </UITypography>
            </UICardDescription>
          )}
          {children}
        </UICardContent>

        {/* Footer with actions */}
        {(actionText || secondaryActionText) && (
          <UICardFooter className="pt-4 border-t border-border/50">
            <UIBox className="flex w-full gap-2">
              {actionText && onAction && (
                <UIButton
                  variant={actionVariant}
                  onClick={onAction}
                  className="flex-1"
                >
                  {actionText}
                </UIButton>
              )}
              {secondaryActionText && onSecondaryAction && (
                <UIButton
                  variant="outline"
                  onClick={onSecondaryAction}
                  className={actionText ? "flex-1" : "w-full"}
                >
                  {secondaryActionText}
                </UIButton>
              )}
            </UIBox>
          </UICardFooter>
        )}
      </UICard>
    )
  }
)

UICardProduct.displayName = "UICardProduct"